import MyMapsPopup from "~/es6/mymaps.popup";
import { node } from "~/util/dom";
import { _ } from "~/i18n";
import { store } from "~/state/store";
import { setSetting } from "~/state/user/state";
import { IUserSpeedData, IUserMaxSpeedData, ESETTINGS_KEYS } from "~/state/user/interfaces";
import { watch, watchSettingsUserSpeedChange, watchSettingsUserMaxSpeedChange } from "~/state/util";
import { Unsubscribe } from "redux";
import { prepareSetting, setUserSettings } from "~/util/providers/mapybox";
import { IConfirmOptions } from "~/report/interfaces";
import { createSectionTitle, createList, userSpeedValidation, setUserSpeedValue, showEditUserSpeed, getUserSpeedData, destroyUserSpeed, getAnalyticsType } from "~/profile/settings/userspeed/popup/userspeed";
import { createMaxSpeedSection, getUserMaxSpeedData, setUserMaxSpeedValue, destroyUserMaxSpeed } from "~/profile/settings/userspeed/popup/maxspeed";
import Tooltip, { ETooltipFlow, ETooltipPosition } from "~/ui/tooltip";

function sendAnalytics(userSpeed, maxSpeed, newUserSpeed, newMaxSpeed) {
	const send = (type, value) => {
		Mapy.getComponent("logger").log("user_setCustomSpeeds_changed", {
			activityType: type,
			// eslint-disable-next-line camelcase
			customSpeed_value: value,
		});
	};

	for (const criterion in userSpeed) {
		if (!newUserSpeed[criterion]) {
			send(getAnalyticsType(parseInt(criterion)), -1);
		}
	}

	for (const criterion in newUserSpeed) {
		if (!userSpeed[criterion] || userSpeed[criterion].value !== newUserSpeed[criterion].value) {
			send(getAnalyticsType(parseInt(criterion)), Math.round(newUserSpeed[criterion].value));
		}
	}

	if ("car" in newMaxSpeed) {
		if ((!("car" in maxSpeed) && newMaxSpeed.car.maxSpeed) || (newMaxSpeed.car.maxSpeed !== maxSpeed.car.maxSpeed)) {
			send("auto", Math.round(newMaxSpeed.car.maxSpeed));
		}
	} else if ("car" in maxSpeed) {
		send("auto", -1);
	}
}

interface ITypeItemsEdit {
	container: HTMLDivElement;
	input: HTMLInputElement;
	reset: HTMLSpanElement | SVGElement;
	tooltip: Tooltip;
}

export interface ITypeItems {
	container: HTMLLIElement;
	textValue: HTMLDivElement;
	editValue: ITypeItemsEdit;
}

export interface ITypeMaxSpeedItem extends ITypeItemsEdit {
	toggle: HTMLMapyToggleElement;
}

interface IEditData {
	maxSpeed?: ITypeMaxSpeedItem;
	items: Array<ITypeItems>;
	buttonsContainer: HTMLDivElement;
	saveButton: HTMLButtonElement;
	editMessage: HTMLParagraphElement;
}

export const CHANGED_CLASS = "changed";
const validationState = {
	maxSpeed: true,
	userSpeed: true,
};

export class SettingsUserSpeedPopup extends MyMapsPopup {
	#editData: IEditData;

	#storeUnsubscribeUserSpeed: Unsubscribe;

	#storeUnsubscribeUserMaxSpeed: Unsubscribe;

	#settingsUserSpeedValue: IUserSpeedData;

	#settingsUserMaxSpeedValue: IUserMaxSpeedData;

	#content: HTMLDivElement;

	#userSpeedContainer: HTMLDivElement;

	#maxSpeedContainer: HTMLDivElement;

	#overlay: HTMLDivElement;

	#confirmation: HTMLDivElement;

	#tooltip: Tooltip;

	#closeCallback: () => void;

	constructor(closeCallback: () => void = () => {}) {
		super({
			closeSvg: "close-v2",
		});
		this._dom.container.classList.add("settings-user-speed");
		this.#settingsUserSpeedValue = {};
		this.#settingsUserMaxSpeedValue = {};
		this.#content = node("div", { className: "settings-user-speed__content" });
		this.#userSpeedContainer = node("div", { className: "settings-user-speed__container" });
		this.#maxSpeedContainer = node("div", { className: "settings-user-max-speed__container" });
		this.#closeCallback = closeCallback;

		this.#editData = null;
		this.#tooltip = null;

		this.#build();

		const wtUserSpeed = watch(store.getState, "user.settings", watchSettingsUserSpeedChange);
		const wtUserMaxSpeed = watch(store.getState, "user.settings", watchSettingsUserMaxSpeedChange);

		this.#storeUnsubscribeUserSpeed = store.subscribe(wtUserSpeed(newValue => {
			const newUserSpeedData = newValue.find(item => item.title === ESETTINGS_KEYS.ROUTING_CRITERION_SPEED)?.data?.value || {};

			this.#settingsUserSpeedValueChange(newUserSpeedData);
		}));

		this.#storeUnsubscribeUserMaxSpeed = store.subscribe(wtUserMaxSpeed(newValue => {
			const newUserMaxSpeedData = (newValue.find(item => item.title === ESETTINGS_KEYS.ROUTING_MAX_SPEED)?.data?.value || {}) as IUserMaxSpeedData;

			this.#settingsUserMaxSpeedValueChange(newUserMaxSpeedData);
		}));

		//window.addEventListener("resize", this.#checkOverflow);
	}

	destroy() {
		this.#storeUnsubscribeUserSpeed();
		this.#storeUnsubscribeUserMaxSpeed();

		destroyUserSpeed(this.#editData.items);
		destroyUserMaxSpeed(this.#editData.maxSpeed);
	}

	open() {
		super.open();

		this.#checkContentScroll();
	}

	close() {
		this.#resetAll();
		super.close();
		this.#closeCallback();
	}

	handleEvent(event) {
		let handle = false;

		switch (event.type) {
			case "click":
				if (this._opts.userClose && (event.target === this._dom.cover || event.target === this._dom.closeBtn || this._dom.closeBtn.contains(event.target))) {
					event.stopPropagation();
					if (
						this.#userSpeedContainer.classList.contains("edit")
						&& (
							JSON.stringify(getUserSpeedData(this.#editData.items)) !== JSON.stringify(this.#settingsUserSpeedValue)
							|| JSON.stringify(getUserMaxSpeedData(this.#editData.maxSpeed)) !== JSON.stringify(this.#settingsUserMaxSpeedValue)
						)
					) {
						this.#showConfirmation({
							text: _("profile.settings.userspeed.confirmText"),
							confirmText: _("profile.settings.userspeed.throwAway"),
							rejectText: _("profile.settings.userspeed.back"),
						});
					} else {
						this.close();
					}

					handle = true;
				}
				break;
			default:
				break;
		}

		if (!handle) {
			super.handleEvent(event);
		}
	}

	#build() {
		const { user, settings } = store.getState().user;
		const premiumUser = user.premium;
		const title = node("h4", {}, _("profile.settings.userspeed.title"));
		const editMessage = node("p", { className: "settings-user-speed__paragraph-valid" }, _("profile.settings.userspeed.editMessage"));
		const buttonsContainer = node("div", { className: "settings-user-speed__buttons" });
		const saveBtn = node("button", { type: "button", className: "save", textContent: _("save") });
		const cancelBtn = node("button", { type: "button", className: "cancel", textContent: premiumUser ? _("cancel") : _("close") });

		this.#settingsUserSpeedValue = settings.find(item => item.title === ESETTINGS_KEYS.ROUTING_CRITERION_SPEED)?.data?.value as IUserSpeedData || {};
		this.#settingsUserMaxSpeedValue = settings.find(item => item.title === ESETTINGS_KEYS.ROUTING_MAX_SPEED)?.data?.value as IUserMaxSpeedData || {};

		saveBtn.setAttribute("data-log-ignore", "1");

		saveBtn.addEventListener("click", async () => {
			await this.#save();

			this.close();
		});

		cancelBtn.addEventListener("click", () => {
			if (premiumUser) {
				this.#resetAll();
			}

			this.close();
		});

		premiumUser && buttonsContainer.append(saveBtn);
		buttonsContainer.append(cancelBtn);

		this.#editData = {
			buttonsContainer: buttonsContainer,
			saveButton: saveBtn,
			editMessage,
			items: [],
		};

		const maxSpeed = createMaxSpeedSection(this.#settingsUserMaxSpeedValue, () => {
			this.close();
		}, premiumUser, invalid => {
			validationState.maxSpeed = !invalid;
			this.#validation();
		});

		this.#editData.maxSpeed = maxSpeed.item;

		this.#maxSpeedContainer.append(maxSpeed.container);

		const list = createList(this.#settingsUserSpeedValue, invalid => {
			validationState.userSpeed = !invalid;
			this.#validation();
		}, premiumUser);

		this.#editData.items = list.editItems;

		this.#userSpeedContainer.append(createSectionTitle(() => {
			this.close();
		}, premiumUser), list.container);

		premiumUser && this.#showEditUserSpeed();

		this.#overlay = node("div", { className: "overlay" });
		this.#confirmation = node("div", { className: "confirmation" });

		this.#content.append(this.#maxSpeedContainer, this.#userSpeedContainer);
		this.#content.addEventListener("scroll", () => {
			this.#checkContentScroll();
		});

		this._dom.container.append(title, this.#content, buttonsContainer, this.#overlay);
	}

	#settingsUserSpeedValueChange(userSettingsValue) {
		this.#settingsUserSpeedValue = userSettingsValue;

		setUserSpeedValue(this.#settingsUserSpeedValue, this.#editData.items);
	}

	#settingsUserMaxSpeedValueChange(userSettingsValue) {
		this.#settingsUserMaxSpeedValue = userSettingsValue;

		setUserMaxSpeedValue(this.#settingsUserMaxSpeedValue, this.#editData.maxSpeed);
	}

	#showEditUserSpeed() {
		const show = !this.#userSpeedContainer.classList.contains("edit");

		if (show) {
			this.#userSpeedContainer.append(this.#editData.editMessage);
		} else {
			this.#editData.editMessage.remove();
		}

		showEditUserSpeed(this.#editData.items, show);

		this.#userSpeedContainer.classList[show ? "add" : "remove"]("edit");
	}

	async #save() {
		const userSpeed = getUserSpeedData(this.#editData.items);
		const userMaxSpeed = getUserMaxSpeedData(this.#editData.maxSpeed);
		const settings = prepareSetting(ESETTINGS_KEYS.ROUTING_CRITERION_SPEED, userSpeed);
		const settingsMaxSpeed = prepareSetting(ESETTINGS_KEYS.ROUTING_MAX_SPEED, userMaxSpeed);

		sendAnalytics(this.#settingsUserSpeedValue, this.#settingsUserMaxSpeedValue, userSpeed, userMaxSpeed);

		await setUserSettings([settings, settingsMaxSpeed]);

		store.dispatch(setSetting(settings));
		store.dispatch(setSetting(settingsMaxSpeed));
	}

	#resetAll() {
		if (this.#userSpeedContainer.classList.contains("edit")) {
			this.#settingsUserMaxSpeedValueChange(this.#settingsUserMaxSpeedValue);
			this.#settingsUserSpeedValueChange(this.#settingsUserSpeedValue);
			userSpeedValidation(this.#editData.items, invalid => {
				validationState.userSpeed = !invalid;
				this.#validation();
			});
		}
	}

	#disableSaveButton(disabled) {
		this.#editData.saveButton.disabled = disabled;
	}

	#showConfirmation(options: IConfirmOptions) {
		const { text, confirmText, rejectText } = options;
		const nodes = [node("span", { innerText: text })];

		if (confirmText) {
			const btn = node("button", { innerText: confirmText, className: "confirm-button" });

			btn.setAttribute("data-log-ignore", "1");
			nodes.push(btn);

			btn.addEventListener("click", () => {
				this.close();
				this.#hideConfirmation();
			});
		}

		if (rejectText) {
			const btn = node("button", { innerText: rejectText, className: "reject-button" });

			btn.setAttribute("data-log-ignore", "1");
			nodes.push(btn);

			btn.addEventListener("click", () => {
				this.#hideConfirmation();
			});
		}

		this.#confirmation.append(...nodes);
		this.#overlay.classList.add("active");

		if (confirmText || rejectText) {
			this.#confirmation.querySelector("button").focus();
		}

		this.#overlay.append(this.#confirmation);
	}

	#hideConfirmation() {
		this.#overlay.classList.remove("active");

		setTimeout(() => {
			this.#confirmation.remove();
			this.#confirmation.innerHTML = "";
		}, 300);
	}

	#checkContentScroll() {
		this.#content.classList[this.#content.scrollTop > 0 ? "add" : "remove"]("scroll-top");
		this.#editData.buttonsContainer.classList[this.#content.clientHeight + this.#content.scrollTop === this.#content.scrollHeight ? "remove" : "add"]("scroll-bottom");
	}

	#validation() {
		this.#disableSaveButton(!(validationState.maxSpeed && validationState.userSpeed));

		if (!validationState.maxSpeed) {
			if (!this.#tooltip) {
				this.#tooltip = new Tooltip({
					anchor: this.#editData.saveButton,
					trigger: this.#editData.saveButton,
					content: node("p", { innerHTML: _("profile.settings.userspeed.maxSpeed.validationTooltip") }),
					arrow: false,
					flow: ETooltipFlow.CENTER,
					position: ETooltipPosition.TOP,
					offset: [2, 0, 0, 0],
					hideTimeout: 0,
					width: 240,
				});
			}
		} else if (this.#tooltip) {
			this.#tooltip.destroy();
		}
	}
}
